// TopBar notifications bell + dropdown.
//
// Backend gap (2026-04-26): the only list-shaped endpoint exposed by
// notificationsRouter is GET /api/notifications/history, which returns
// the last 25 CaseAlert rows: the mail deliveries and the form notices. The
// shape has no `link` and no read/unread state. Each kind knows where its
// row leads: a critical alert to its matter by eId (the case tag marks
// those rows), a form notice to Settings > Forms, a digest nowhere;
// marks-read are tracked client-side in localStorage. See
// `api/notifications.ts` for the swap-in path when the backend grows a
// real in-app notification model.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertCircle, Mail, Inbox, FileText, type LucideIcon } from "lucide-react";
import type { NotificationAlertType } from "@duralex/shared";
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  type NotificationItemView,
} from "../../api/notifications";
import { formatRelative } from "../../util/time";

const MAX_VISIBLE = 10;

function deliveryWords(item: NotificationItemView): string {
  return item.delivered === false ? "Delivery failed" : "Sent";
}

// One entry per kind, so a kind the server adds fails the typecheck here
// until the bell knows where it leads.
type Kind = {
  Icon: LucideIcon;
  tone: "critical" | "digest" | "forms";
  meta: (item: NotificationItemView) => string;
  to: (item: NotificationItemView) => string | null;
};

const KIND: Record<NotificationAlertType, Kind> = {
  critical: {
    Icon: AlertCircle,
    tone: "critical",
    meta: deliveryWords,
    to: (item) => (item.caseEId ? `/matter/${item.caseEId}` : null),
  },
  digest: { Icon: Mail, tone: "digest", meta: deliveryWords, to: () => null },
  form_update: {
    Icon: FileText,
    tone: "forms",
    meta: () => "Settings > Forms",
    to: () => "/settings/forms",
  },
};

// A kind the server grew after this bundle was built shows its words and
// its matter and claims nothing about a delivery; a row that throws would
// blank the whole app.
const LATER: Kind = {
  Icon: Bell,
  tone: "digest",
  meta: (item) => (item.delivered === null ? "" : deliveryWords(item)),
  to: (item) => (item.caseEId ? `/matter/${item.caseEId}` : null),
};

function kindOf(type: NotificationAlertType): Kind {
  return KIND[type] ?? LATER;
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { items, unreadCount, isLoading, isError, refetch } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Click-outside + Esc to close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = items.slice(0, MAX_VISIBLE);
  const badgeText = unreadCount > 9 ? "9+" : unreadCount > 0 ? String(unreadCount) : "";

  const onItemClick = (n: NotificationItemView) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    const to = kindOf(n.alertType).to(n);
    if (to) navigate(to);
  };

  const onMarkAll = () => {
    if (items.length === 0) return;
    markAllRead.mutate(items.map((n) => n.id));
  };

  return (
    <div ref={containerRef} className="cc-notif" style={{ position: "relative" }}>
      <button
        type="button"
        className="cc-notif-btn"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={16} strokeWidth={2} />
        {badgeText && (
          <span className="cc-notif-badge" aria-hidden="true">
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div className="cc-notif-panel" role="menu">
          <div className="cc-notif-head">
            <span className="cc-notif-title">Notifications</span>
            {unreadCount > 0 && <span className="cc-notif-unread-pill">{unreadCount} unread</span>}
          </div>

          <div className="cc-notif-list">
            {isLoading && <NotifLoading />}
            {isError && (
              <NotifError
                onRetry={() => {
                  refetch();
                }}
              />
            )}
            {!isLoading && !isError && visible.length === 0 && <NotifEmpty />}
            {!isLoading &&
              !isError &&
              visible.map((n) => <NotifRow key={n.id} item={n} onClick={() => onItemClick(n)} />)}
          </div>

          <div className="cc-notif-foot">
            <button
              type="button"
              className="cc-notif-foot-btn"
              onClick={onMarkAll}
              disabled={unreadCount === 0 || markAllRead.isPending}
            >
              Mark all read
            </button>
            <button type="button" className="cc-notif-foot-btn" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ item, onClick }: { item: NotificationItemView; onClick: () => void }) {
  const kind = kindOf(item.alertType);
  const Icon = kind.Icon;
  return (
    <button
      type="button"
      className={`cc-notif-row${item.read ? " read" : ""}`}
      onClick={onClick}
      role="menuitem"
    >
      <span className={`cc-notif-icon ${kind.tone}`}>
        <Icon size={14} strokeWidth={2} />
      </span>
      <span className="cc-notif-body">
        <span className="cc-notif-row-title">{item.subject || "(no subject)"}</span>
        <span className="cc-notif-row-meta">
          <span>{kind.meta(item)}</span>
          {item.caseEId && <span className="cc-notif-row-tag">case</span>}
        </span>
      </span>
      <span className="cc-notif-row-time">{formatRelative(item.sentAt)}</span>
    </button>
  );
}

function NotifLoading() {
  return (
    <div className="cc-notif-state">
      {[0, 1, 2].map((i) => (
        <div key={i} className="cc-notif-row" aria-hidden="true">
          <span className="cc-notif-icon digest" style={{ opacity: 0.35 }} />
          <span className="cc-notif-body">
            <span
              style={{
                display: "block",
                height: 10,
                width: "70%",
                background: "var(--line)",
                borderRadius: 4,
                marginBottom: 6,
              }}
            />
            <span
              style={{
                display: "block",
                height: 8,
                width: "40%",
                background: "var(--line-2)",
                borderRadius: 4,
              }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function NotifEmpty() {
  return (
    <div className="cc-notif-state cc-notif-empty">
      <Inbox size={20} strokeWidth={1.6} aria-hidden="true" />
      <span>No notifications yet</span>
    </div>
  );
}

function NotifError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="cc-notif-state cc-notif-empty">
      <AlertCircle size={20} strokeWidth={1.6} aria-hidden="true" />
      <span>Couldn’t load notifications.</span>
      <button type="button" className="cc-notif-foot-btn" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}
